import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MDBContainer } from "mdbreact";
import NumberFormat from "react-number-format";
import "./TransactionDetail.css";

const Currency = ({ value }) => (
  <NumberFormat
    value={Number(value) || 0}
    displayType="text"
    thousandSeparator="."
    decimalSeparator=","
    prefix="Rp "
  />
);

const InfoItem = ({ icon, label, children }) => (
  <div className="invoice-detail__info-item">
    <div className="invoice-detail__info-icon">
      <i className={icon} aria-hidden="true" />
    </div>
    <div>
      <div className="invoice-detail__info-label">{label}</div>
      <div className="invoice-detail__info-value">{children || "-"}</div>
    </div>
  </div>
);

const formatAddress = (customer) => {
  if (!customer || !customer.address) {
    return "Alamat tidak tersedia";
  }

  const address = customer.address;
  const zip = address.zip || {};

  return [
    address.detail || address.address,
    zip.sub_district,
    zip.district,
    zip.sub_province,
    zip.province,
    zip.zip,
  ]
    .filter(Boolean)
    .join(", ");
};

const getStatusTone = (status) => {
  const value = String(status || "");

  if (value === "5") return "success";
  if (value === "98" || value === "99") return "danger";
  if (value === "3" || value === "4") return "info";
  return "warning";
};

const terminalStatuses = ["5", "98", "99"];

const getNextStatus = (invoice) => {
  if (!invoice) return null;

  const status = String(invoice.status || "");
  if (terminalStatuses.includes(status)) return null;
  if (status === "3") return null;
  if (status === "1") return { id: "2", name: "Diproses Penjual" };
  if (status === "2") {
    if (invoice.courier_code === "self_pickup") return { id: "6", name: "Siap Diambil" };
    if (invoice.courier_code === "self_courier") return { id: "7", name: "Dalam Pengiriman" };
    return { id: "3", name: "Dalam Pengiriman" };
  }
  if (["6", "7"].includes(status)) return { id: "4", name: "Menunggu Konfirmasi Selesai" };
  if (status === "4") return { id: "5", name: "Pesanan Selesai" };

  return null;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
};

const TransactionDetail = (props) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionError, setRejectionError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const { invoiceId } = props.match.params;

  useEffect(() => {
    let active = true;

    const fetchInvoice = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axios.get(
          global.variable.LINK_API +
            `report/invoice/${encodeURIComponent(invoiceId)}`,
          { ...global.variable.HEADERS_BEARER, cancelKey: "transaction-detail" }
        );

        if (active) setInvoice(response.data);
      } catch (requestError) {
        if (active) {
          const responseData = requestError.response && requestError.response.data;
          const message =
            responseData && (responseData.message || responseData.errors);
          setError(
            typeof message === "string"
              ? message
              : "Detail invoice tidak dapat dimuat. Silakan coba lagi."
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchInvoice();

    return () => {
      active = false;
    };
  }, [invoiceId, reloadKey]);

  const products = useMemo(() => {
    if (!invoice) return [];
    if (Array.isArray(invoice.item_details)) return invoice.item_details;

    return invoice.cart && Array.isArray(invoice.cart.detail_checked)
      ? invoice.cart.detail_checked
      : [];
  }, [invoice]);

  const productTotal = useMemo(() => {
    return products.reduce((total, item) => {
      const product = item.product || {};
      const variant = item.varian;
      const quantity = Number(item.qty) || 0;
      const price = Number(
        variant
          ? variant.fix_price || variant.price
          : product.fix_price || product.price
      ) || 0;

      return total + price * quantity;
    }, 0);
  }, [products]);

  if (loading) {
    return (
      <MDBContainer className="invoice-detail-page">
        <div className="invoice-detail__state-card">
          <div className="invoice-detail__spinner" />
          <h5>Memuat detail invoice</h5>
          <p>Mohon tunggu sebentar...</p>
        </div>
      </MDBContainer>
    );
  }

  if (error || !invoice) {
    return (
      <MDBContainer className="invoice-detail-page">
        <div className="invoice-detail__state-card invoice-detail__state-card--error">
          <div className="invoice-detail__state-icon">
            <i className="fas fa-exclamation-circle" aria-hidden="true" />
          </div>
          <h5>Detail invoice tidak tersedia</h5>
          <p>{error || "Invoice tidak ditemukan."}</p>
          <button
            type="button"
            className="invoice-detail__button invoice-detail__button--primary"
            onClick={() => props.history.goBack()}
          >
            Kembali
          </button>
        </div>
      </MDBContainer>
    );
  }

  const transactionDetails = invoice.transaction_details || {};
  const isKliksekolah = invoice.source === "kliksekolah";
  const seller = invoice.seller_details || {};
  const customer = invoice.customer_details || {};
  const paymentType = transactionDetails.payment_type || {};
  const courierDetail = invoice.cart && invoice.cart.courier_detail
    ? invoice.cart.courier_detail
    : {};
  const grossAmount = invoice.gross_amount || transactionDetails.gross_amount || 0;
  const orderId = invoice.order_id || transactionDetails.order_id || "-";
  const discount = Number(invoice.total_discount) || 0;
  const statusTone = getStatusTone(invoice.status);
  const waitingForCourier = String(invoice.status || "") === "3";
  const canReject = !isKliksekolah && ["1", "2", "4", "6", "7", "98"].includes(String(invoice.status || ""));
  const customerName = [customer.first_name, customer.last_name]
    .filter(Boolean)
    .join(" ");
  const nextStatus = isKliksekolah ? null : getNextStatus(invoice);

  const advanceStatus = async () => {
    setAdvancing(true);
    setActionError("");

    try {
      await axios.post(
        global.variable.LINK_API +
          `report/invoice/${encodeURIComponent(invoice.invoice_id || invoiceId)}/next-status`,
        {},
        global.variable.HEADERS_BEARER
      );
      setConfirmationOpen(false);
      setActionSuccess(`Status berhasil dilanjutkan ke ${nextStatus.name}.`);
      setReloadKey(value => value + 1);
    } catch (requestError) {
      const responseData = requestError.response && requestError.response.data;
      const responseErrors = responseData && responseData.errors;
      const message = responseData && responseData.message;
      setActionError(
        typeof message === "string"
          ? message
          : Array.isArray(responseErrors)
            ? responseErrors.join(" ")
            : "Status invoice tidak dapat dilanjutkan. Silakan coba kembali."
      );
    } finally {
      setAdvancing(false);
    }
  };

  const rejectInvoice = async () => {
    setRejecting(true);
    setRejectionError("");

    try {
      await axios.post(
        global.variable.LINK_API +
          `report/invoice/${encodeURIComponent(invoice.invoice_id || invoiceId)}/reject`,
        {},
        global.variable.HEADERS_BEARER
      );
      setRejectionOpen(false);
      setActionSuccess("Invoice berhasil ditolak dan diproses sebagai pembatalan/refund.");
      setReloadKey(value => value + 1);
    } catch (requestError) {
      const responseData = requestError.response && requestError.response.data;
      const message = responseData && responseData.message;
      setRejectionError(
        typeof message === "string"
          ? message
          : "Invoice tidak dapat ditolak. Silakan coba kembali."
      );
    } finally {
      setRejecting(false);
    }
  };

  return (
    <MDBContainer className="invoice-detail-page">
      <div className="invoice-detail__topbar">
        <button
          type="button"
          className="invoice-detail__back"
          onClick={() => props.history.goBack()}
        >
          <i className="fas fa-arrow-left" aria-hidden="true" />
          Kembali ke daftar
        </button>
        <span className="invoice-detail__breadcrumb">
          Transaksi <i className="fas fa-chevron-right" aria-hidden="true" /> Detail invoice
        </span>
      </div>

      <section className="invoice-detail__hero">
        <div>
          <div className="invoice-detail__eyebrow">{isKliksekolah ? "INVOICE KLIKSEKOLAH" : "INVOICE"}</div>
          <h1>{invoice.invoice_id || invoiceId}</h1>
          <div className="invoice-detail__order">
            {isKliksekolah ? "Referensi siswa" : "Order ID"} <strong>{orderId}</strong>
          </div>
        </div>
        <div className="invoice-detail__hero-actions">
          <div className={`invoice-detail__status invoice-detail__status--${statusTone}`}>
            <span className="invoice-detail__status-dot" />
            {invoice.status_name || `Status ${invoice.status}`}
          </div>
          {(nextStatus || canReject) && (
            <div className="invoice-detail__hero-action-row">
              {canReject && (
                <button
                  type="button"
                  className="invoice-detail__reject-button"
                  onClick={() => {
                    setRejectionError("");
                    setRejectionOpen(true);
                  }}
                >
                  <i className="fas fa-ban" aria-hidden="true" />
                  Tolak
                </button>
              )}
              {nextStatus && (
                <button
                  type="button"
                  className="invoice-detail__next-button"
                  onClick={() => {
                    setActionError("");
                    setConfirmationOpen(true);
                  }}
                >
                  Lanjutkan ke {nextStatus.name}
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {actionSuccess && (
        <div className="invoice-detail__alert invoice-detail__alert--success" role="status">
          <i className="fas fa-check-circle" aria-hidden="true" />
          <span>{actionSuccess}</span>
          <button type="button" onClick={() => setActionSuccess("")} aria-label="Tutup notifikasi">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
      )}

      {waitingForCourier && (
        <div className="invoice-detail__alert invoice-detail__alert--info" role="status">
          <i className="fas fa-truck" aria-hidden="true" />
          <span>Status tidak dapat diubah manual selama pesanan dalam pengiriman. Sistem akan memperbaruinya setelah menerima data pesanan sampai dari kurir.</span>
        </div>
      )}

      <section className="invoice-detail__summary-grid">
        <div className="invoice-detail__summary-card">
          <div className="invoice-detail__summary-icon invoice-detail__summary-icon--purple">
            <i className="fas fa-wallet" aria-hidden="true" />
          </div>
          <div>
            <span>Total invoice</span>
            <strong><Currency value={grossAmount} /></strong>
          </div>
        </div>
        <div className="invoice-detail__summary-card">
          <div className="invoice-detail__summary-icon invoice-detail__summary-icon--blue">
            <i className="fas fa-credit-card" aria-hidden="true" />
          </div>
          <div>
            <span>Metode pembayaran</span>
            <strong>{paymentType.name || invoice.payment_type || "-"}</strong>
          </div>
        </div>
        <div className="invoice-detail__summary-card">
          <div className="invoice-detail__summary-icon invoice-detail__summary-icon--green">
            <i className="fas fa-box" aria-hidden="true" />
          </div>
          <div>
            <span>{isKliksekolah ? "Jumlah tagihan" : "Jumlah produk"}</span>
            <strong>{products.length} {isKliksekolah ? "tagihan" : "produk"}</strong>
          </div>
        </div>
      </section>

      <section className="invoice-detail__content-grid">
        <div className="invoice-detail__panel">
          <div className="invoice-detail__panel-header">
            <div>
              <span className="invoice-detail__section-kicker">INFORMASI</span>
              <h2>{isKliksekolah ? "Sekolah dan siswa" : "Penjual dan pembeli"}</h2>
            </div>
          </div>
          <div className="invoice-detail__party-grid">
            <div className="invoice-detail__party">
              <div className="invoice-detail__party-title">
                <span className="invoice-detail__avatar invoice-detail__avatar--seller">
                  <i className="fas fa-store" aria-hidden="true" />
                </span>
                <div>
                  <span>{isKliksekolah ? "Layanan sekolah" : "Penjual"}</span>
                  <strong>{seller.name || invoice.merchant_name || "-"}</strong>
                </div>
              </div>
              <InfoItem icon="fas fa-map-marker-alt" label={isKliksekolah ? "Unit sekolah" : "Lokasi toko"}>
                {seller.location && (seller.location.detail || seller.location.address)}
              </InfoItem>
              <InfoItem icon="fas fa-phone-alt" label="Telepon">
                {seller.location && seller.location.hp}
              </InfoItem>
            </div>

            <div className="invoice-detail__party">
              <div className="invoice-detail__party-title">
                <span className="invoice-detail__avatar invoice-detail__avatar--customer">
                  <i className="fas fa-user" aria-hidden="true" />
                </span>
                <div>
                  <span>{isKliksekolah ? "Siswa" : "Pembeli"}</span>
                  <strong>{customerName || "-"}</strong>
                </div>
              </div>
              <InfoItem icon="fas fa-map-marker-alt" label={isKliksekolah ? "Alamat penagihan" : "Alamat pengiriman"}>
                {formatAddress(customer)}
              </InfoItem>
              <InfoItem icon="fas fa-phone-alt" label="Telepon">
                {customer.phone || (customer.address && customer.address.hp)}
              </InfoItem>
            </div>
          </div>
        </div>

        <aside className="invoice-detail__panel invoice-detail__shipping-panel">
          <div className="invoice-detail__panel-header">
            <div>
              <span className="invoice-detail__section-kicker">{isKliksekolah ? "PEMBAYARAN" : "PENGIRIMAN"}</span>
              <h2>{isKliksekolah ? "Informasi pembayaran" : "Informasi kurir"}</h2>
            </div>
          </div>
          {isKliksekolah ? <>
            <InfoItem icon="fas fa-credit-card" label="Channel">{invoice.payment_gateway || paymentType.name}</InfoItem>
            <InfoItem icon="far fa-calendar-check" label="Tanggal pembayaran">{formatDateTime(invoice.payments_date)}</InfoItem>
            <InfoItem icon="far fa-calendar-alt" label="Jatuh tempo">{formatDateTime(invoice.payments_due_date)}</InfoItem>
            {invoice.payment_reference && <InfoItem icon="fas fa-receipt" label="Nomor bukti">{invoice.payment_reference}</InfoItem>}
          </> : <>
            <InfoItem icon="fas fa-truck" label="Kurir">{invoice.courier_name || courierDetail.courier_name}</InfoItem>
            <InfoItem icon="fas fa-shipping-fast" label="Layanan">{invoice.courier_service || courierDetail.courier_service}</InfoItem>
            <InfoItem icon="far fa-clock" label="Estimasi">{courierDetail.estimate || "-"}</InfoItem>
            {invoice.awb && <InfoItem icon="fas fa-barcode" label="Nomor resi">{invoice.awb}</InfoItem>}
          </>}
        </aside>
      </section>

      <section className="invoice-detail__panel invoice-detail__products-panel">
        <div className="invoice-detail__panel-header invoice-detail__products-header">
          <div>
            <span className="invoice-detail__section-kicker">{isKliksekolah ? "RINCIAN TAGIHAN" : "RINCIAN PESANAN"}</span>
            <h2>{isKliksekolah ? "Detail pembayaran pendidikan" : "Daftar produk"}</h2>
          </div>
          <span>{products.length} item</span>
        </div>

        <div className="invoice-detail__table-wrap">
          <table className="invoice-detail__table">
            <thead>
              <tr>
                <th>Produk</th>
                <th>Harga satuan</th>
                <th>Jumlah</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="4" className="invoice-detail__empty-row">
                    Detail produk tidak tersedia.
                  </td>
                </tr>
              ) : (
                products.map((item, index) => {
                  const product = item.product || {};
                  const variant = item.varian;
                  const quantity = Number(item.qty) || 0;
                  const price = Number(
                    variant
                      ? variant.fix_price || variant.price
                      : product.fix_price || product.price
                  ) || 0;
                  const variantName = variant
                    ? [variant.name1, variant.name2].filter(Boolean).join(" / ")
                    : "";

                  return (
                    <tr key={item.id || `${product.id || "product"}-${index}`}>
                      <td>
                        <div className="invoice-detail__product">
                          <div className="invoice-detail__product-icon">
                            <i className="fas fa-box-open" aria-hidden="true" />
                          </div>
                          <div>
                            <strong>{product.name || "Produk"}</strong>
                            <span>
                              {variantName || "Tanpa varian"}
                              {product.weight ? ` • ${product.weight} gr` : ""}
                            </span>
                            {item.note && <small>Catatan: {item.note}</small>}
                          </div>
                        </div>
                      </td>
                      <td data-label="Harga satuan"><Currency value={price} /></td>
                      <td data-label="Jumlah">{quantity}</td>
                      <td data-label="Subtotal" className="invoice-detail__subtotal">
                        <Currency value={price * quantity} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="invoice-detail__totals-wrap">
          <div className="invoice-detail__payment-note">
            <i className="fas fa-shield-alt" aria-hidden="true" />
            <div>
              <strong>Informasi pembayaran</strong>
              <span>Transaksi diproses menggunakan {paymentType.name || "metode pembayaran terpilih"}.</span>
            </div>
          </div>
          <div className="invoice-detail__totals">
            <div><span>Total harga produk</span><strong><Currency value={productTotal} /></strong></div>
            {!isKliksekolah && <div><span>Ongkos kirim</span><strong><Currency value={invoice.courier_price} /></strong></div>}
            {Number(invoice.courier_insurance) > 0 && (
              <div><span>Asuransi pengiriman</span><strong><Currency value={invoice.courier_insurance} /></strong></div>
            )}
            {discount > 0 && (
              <div className="invoice-detail__discount"><span>Diskon</span><strong>- <Currency value={discount} /></strong></div>
            )}
            <div className="invoice-detail__grand-total">
              <span>Total invoice</span>
              <strong><Currency value={grossAmount} /></strong>
            </div>
          </div>
        </div>
      </section>

      {confirmationOpen && nextStatus && (
        <div className="invoice-detail__modal" role="dialog" aria-modal="true" aria-labelledby="next-status-title">
          <button
            type="button"
            className="invoice-detail__modal-backdrop"
            onClick={() => !advancing && setConfirmationOpen(false)}
            aria-label="Tutup konfirmasi"
          />
          <div className="invoice-detail__modal-panel invoice-detail__modal-panel--advance">
            <header className="invoice-detail__advance-modal-header">
              <span className="invoice-detail__modal-icon">
                <i className="fas fa-arrow-right" />
              </span>
              <div>
                <span className="invoice-detail__section-kicker">PERUBAHAN STATUS</span>
                <h3 id="next-status-title">Konfirmasi status berikutnya</h3>
              </div>
              <button
                type="button"
                className="invoice-detail__modal-close"
                disabled={advancing}
                onClick={() => setConfirmationOpen(false)}
                aria-label="Tutup modal perubahan status"
              >
                <i className="fas fa-times" />
              </button>
            </header>
            <div className="invoice-detail__advance-modal-body">
              <p>Pastikan proses invoice telah selesai pada tahap saat ini sebelum melanjutkan.</p>
              <div className="invoice-detail__status-transition">
                <div>
                  <span>Status saat ini</span>
                  <strong>{invoice.status_name || `Status ${invoice.status}`}</strong>
                </div>
                <i className="fas fa-arrow-right" aria-hidden="true" />
                <div className="invoice-detail__status-transition-next">
                  <span>Status berikutnya</span>
                  <strong>{nextStatus.name}</strong>
                </div>
              </div>
              <div className="invoice-detail__advance-note">
                <i className="fas fa-info-circle" />
                <span>Integrasi kurir, Cekgudang, dan proses terkait akan dijalankan otomatis sesuai tahap invoice.</span>
              </div>
              {actionError && (
                <div className="invoice-detail__modal-error" role="alert">
                  <i className="fas fa-exclamation-circle" /> {actionError}
                </div>
              )}
            </div>
            <footer className="invoice-detail__advance-modal-footer">
              <button
                type="button"
                className="invoice-detail__button invoice-detail__button--secondary"
                disabled={advancing}
                onClick={() => setConfirmationOpen(false)}
              >
                Kembali
              </button>
              <button
                type="button"
                className="invoice-detail__button invoice-detail__button--primary"
                disabled={advancing}
                onClick={advanceStatus}
              >
                {advancing ? <><i className="fas fa-circle-notch fa-spin" /> Memproses...</> : <>Lanjutkan status <i className="fas fa-arrow-right" /></>}
              </button>
            </footer>
          </div>
        </div>
      )}

      {rejectionOpen && canReject && (
        <div className="invoice-detail__modal" role="dialog" aria-modal="true" aria-labelledby="reject-invoice-title">
          <button
            type="button"
            className="invoice-detail__modal-backdrop"
            onClick={() => !rejecting && setRejectionOpen(false)}
            aria-label="Tutup konfirmasi penolakan"
          />
          <div className="invoice-detail__modal-panel invoice-detail__modal-panel--reject">
            <header className="invoice-detail__reject-modal-header">
              <span className="invoice-detail__modal-icon invoice-detail__modal-icon--danger">
                <i className="fas fa-ban" />
              </span>
              <div>
                <span className="invoice-detail__section-kicker">PENOLAKAN INVOICE</span>
                <h3 id="reject-invoice-title">Konfirmasi penolakan</h3>
              </div>
              <button
                type="button"
                className="invoice-detail__modal-close"
                disabled={rejecting}
                onClick={() => setRejectionOpen(false)}
                aria-label="Tutup modal penolakan"
              >
                <i className="fas fa-times" />
              </button>
            </header>
            <div className="invoice-detail__reject-modal-body">
              <p>Periksa kembali invoice sebelum menjalankan pembatalan dan proses refund.</p>
              <div className="invoice-detail__reject-summary">
                <div>
                  <span>Nomor invoice</span>
                  <strong>{invoice.invoice_id || invoiceId}</strong>
                </div>
                <div>
                  <span>Status saat ini</span>
                  <strong>{invoice.status_name || `Status ${invoice.status}`}</strong>
                </div>
              </div>
              <div className="invoice-detail__reject-impact">
                <strong>Dampak setelah dikonfirmasi</strong>
                <ul>
                  <li><i className="fas fa-check" /> Status menjadi Pesanan Dibatalkan (Refund)</li>
                  <li><i className="fas fa-check" /> Saldo atau credit mitra dikembalikan jika digunakan</li>
                  <li><i className="fas fa-check" /> Sales Order Cekgudang dibatalkan jika terkait</li>
                </ul>
              </div>
              <div className="invoice-detail__reject-warning">
                <i className="fas fa-exclamation-triangle" />
                <span>Tindakan ini tidak dapat dibatalkan melalui SPIN Management Portal.</span>
              </div>
              {rejectionError && (
                <div className="invoice-detail__modal-error" role="alert">
                  <i className="fas fa-exclamation-circle" /> {rejectionError}
                </div>
              )}
            </div>
            <footer className="invoice-detail__reject-modal-footer">
              <button
                type="button"
                className="invoice-detail__button invoice-detail__button--secondary"
                disabled={rejecting}
                onClick={() => setRejectionOpen(false)}
              >
                Kembali
              </button>
              <button
                type="button"
                className="invoice-detail__button invoice-detail__button--danger"
                disabled={rejecting}
                onClick={rejectInvoice}
              >
                {rejecting ? <><i className="fas fa-circle-notch fa-spin" /> Memproses...</> : <><i className="fas fa-ban" /> Tolak &amp; proses refund</>}
              </button>
            </footer>
          </div>
        </div>
      )}
    </MDBContainer>
  );
};

export default TransactionDetail;
